import { useMemo, useRef, useState } from "react";
import { createFilterOptions } from "@mui/material";
import { Autocomplete } from "@avenue-8/sidekick-ui";
import { Typography } from "@mui/material";
import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { TextField } from "@shared/components/TextField";
import { COUNTRIES, getBrowserDefaultCountry } from "@shared/utils/countries";
import { PhoneNumberInputRow, CountryOption } from "./PhoneNumberInput.styles";

type CountryOptionData = {
  code: CountryCode;
  name: string;
  callingCode: string;
};

const COUNTRY_OPTIONS: CountryOptionData[] = COUNTRIES.map((country) => ({
  ...country,
  callingCode: getCountryCallingCode(country.code),
}));

const filterCountryOptions = createFilterOptions<CountryOptionData>({
  stringify: (option) => `${option.name} ${option.code} +${option.callingCode}`,
});

const buildValue = (country: CountryCode, rawValue: string) => {
  const digits = rawValue.replace(/\D/g, "");
  if (!digits) return "";

  const parsed = parsePhoneNumberFromString(digits, country);
  return parsed?.number ?? `+${getCountryCallingCode(country)}${digits}`;
};

type PhoneNumberInputProps = {
  label?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  fieldError?: { message?: string };
};

export const PhoneNumberInput = (props: PhoneNumberInputProps) => {
  const { label, name, value, onChange, onBlur, fieldError } = props;

  const [country, setCountry] = useState<CountryCode>(
    () =>
      parsePhoneNumberFromString(value || "")?.country ??
      getBrowserDefaultCountry(),
  );
  const [nationalNumberText, setNationalNumberText] = useState<string>(
    () =>
      parsePhoneNumberFromString(value || "")?.nationalNumber ??
      value?.replace(/\D/g, "") ??
      "",
  );

  const countryOption = useMemo(
    () => COUNTRY_OPTIONS.find((option) => option.code === country),
    [country],
  );

  const numberInputRef = useRef<HTMLInputElement>(null);

  const handleCountryChange = (_: unknown, option: CountryOptionData) => {
    setCountry(option.code);
    onChange(buildValue(option.code, nationalNumberText));
    numberInputRef.current?.focus();
  };

  const handleNumberChange = (rawValue: string) => {
    if (rawValue.trim().startsWith("+")) {
      const parsed = parsePhoneNumberFromString(rawValue.trim());
      if (parsed?.country) {
        setCountry(parsed.country);
        setNationalNumberText(parsed.nationalNumber);
        onChange(parsed.number);
        return;
      }
    }

    setNationalNumberText(rawValue);
    onChange(buildValue(country, rawValue));
  };

  return (
    <PhoneNumberInputRow>
      <Autocomplete
        disableClearable
        autoHighlight
        options={COUNTRY_OPTIONS}
        value={countryOption}
        onChange={handleCountryChange}
        onBlur={onBlur}
        filterOptions={filterCountryOptions}
        getOptionLabel={(option) => `+${option.callingCode}`}
        getOptionKey={(option) => option.code}
        isOptionEqualToValue={(option, selected) =>
          option.code === selected.code
        }
        renderOption={({ key, ...optionProps }, option) => (
          <CountryOption key={key} {...optionProps}>
            <Typography variant="body1">{option.name}</Typography>
            <Typography variant="body2">+{option.callingCode}</Typography>
          </CountryOption>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Country" hideHelperTextSpace />
        )}
        slotProps={{ paper: { sx: { width: "20rem" } } }}
        sx={{ width: "5rem", flexShrink: 0 }}
      />
      <TextField
        fullWidth
        type="tel"
        label={label}
        name={name}
        inputRef={numberInputRef}
        value={nationalNumberText}
        onChange={(event) => handleNumberChange(event.target.value)}
        onBlur={onBlur}
        fieldError={fieldError}
      />
    </PhoneNumberInputRow>
  );
};
